"use client";

import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useState, useTransition } from "react";

import { Select } from "@/components/office/ui";

type Props = {
  value: number;
  options: readonly number[];
  defaultValue: number;
  ariaLabel: string;
};

/**
 * The page-size control's behavior over the house hairline `Select`: it
 * updates the `?per=N` query param and pushes with `scroll: false`, so the
 * page doesn't snap back to the top of the viewport when a new size is
 * selected. The look is the house select's and nothing of its own — the
 * rainbow underline included: like every hairline select that acts on change,
 * it runs while the change is in flight, here until the list comes back at
 * its new size. The select shows the size asked for meanwhile, not the one
 * still on screen.
 *
 * Drops the `per` param when the choice equals the default and the
 * `page` param always (a new page size resets to the first page).
 */
export function PageSizeSelect({
  value,
  options,
  defaultValue,
  ariaLabel,
}: Props) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [pending, startTransition] = useTransition();
  // Optimistic: the size asked for, until the page that has it arrives and
  // `value` catches up (the adjust-during-render pattern).
  const [shown, setShown] = useState(value);
  const [prevValue, setPrevValue] = useState(value);
  if (prevValue !== value) {
    setPrevValue(value);
    setShown(value);
  }

  const onChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const next = parseInt(e.target.value, 10);
    setShown(next);
    const params = new URLSearchParams(searchParams.toString());
    if (next === defaultValue) params.delete("per");
    else params.set("per", String(next));
    params.delete("page");
    const qs = params.toString();
    startTransition(() => {
      router.push(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
    });
  };

  return (
    <Select
      variant="compact"
      busy={pending}
      value={shown}
      onChange={onChange}
      aria-label={ariaLabel}
    >
      {options.map((n) => (
        <option key={n} value={n}>
          {String(n).padStart(2, "0")}
        </option>
      ))}
    </Select>
  );
}
